import type { ClipboardEvent, KeyboardEvent } from 'react'
import { useCallback, useEffect, useRef } from 'react'

import { DEFAULT_MAX_HEIGHT, MIN_HEIGHT } from './chat-input-composer.config'
import { handleImagePaste } from './chat-input-paste.helper'

interface UseChatInputTextareaParams {
  value: string
  maxHeight: number
  isDisabled: boolean
  isLoading: boolean
  attachmentCount: number
  onChange: (value: string) => void
  onSend: () => void
  onFilesSelected?: (fileList: File[]) => void
}

export interface UseChatInputTextareaReturn {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  handleKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void
  handlePaste: (e: ClipboardEvent<HTMLTextAreaElement>) => void
  focus: () => void
}

const adjustTextareaHeight = (textarea: HTMLTextAreaElement, maxHeight: number): void => {
  textarea.style.height = 'auto'
  const scrollHeight = textarea.scrollHeight
  const newHeight = Math.min(Math.max(scrollHeight, MIN_HEIGHT), maxHeight)
  textarea.style.height = `${newHeight}px`
  textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden'
}

export const useChatInputTextarea = ({
  value,
  maxHeight = DEFAULT_MAX_HEIGHT,
  isDisabled,
  isLoading,
  attachmentCount,
  onChange,
  onSend,
  onFilesSelected,
}: UseChatInputTextareaParams): UseChatInputTextareaReturn => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (textareaRef.current && value !== undefined) {
      adjustTextareaHeight(textareaRef.current, maxHeight)
    }
  }, [value, maxHeight])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value),
    [onChange]
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        e.key === 'Enter' &&
        !e.shiftKey &&
        !isDisabled &&
        !isLoading &&
        (value.trim() || attachmentCount > 0)
      ) {
        e.preventDefault()
        onSend()
      }
    },
    [isDisabled, isLoading, onSend, value, attachmentCount]
  )

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLTextAreaElement>) => handleImagePaste(e, onFilesSelected),
    [onFilesSelected]
  )

  const focus = useCallback(() => textareaRef.current?.focus(), [])

  return { textareaRef, handleChange, handleKeyDown, handlePaste, focus }
}
